import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import api from '@/api'

export type RecipeListItem = {
  id: string
  title: string
  createdAt: string
}

export type Recipe = {
  id: string
  title: string
  createdAt: string
  shareToken?: string
  video: {
    url: string
  }
  ingredients: {
    name: string
    amount: string
  }[]
  steps: {
    order: number
    action: string
    image?: string
  }[]
  nutrition: {
    calories: number
    protein: number
    carbs: number
    fat: number
  }
}

export const useMyRecipesQuery = () => {
  return useQuery<RecipeListItem[]>({
    queryKey: ['recipes', 'me'],
    queryFn: async () => {
      const res = await api.get('/recipes')
      return res.data
    },
  })
}

export const useRecipeQuery = ({ id, shareToken }: { id?: string; shareToken?: string }) => {
  return useSuspenseQuery<Recipe>({
    queryKey: ['recipes', 'me', id || shareToken],
    queryFn: async () => {
      const url = id ? `/recipes/${id}` : `/recipes/shared/${shareToken}`
      const res = await api.get(url)
      return res.data
    },
  })
}

type ExtractRecipeParams = {
  url: string
  language: string
}

export const useExtractRecipeMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ url, language }: ExtractRecipeParams) => {
      const res = await api.get<Recipe>('/recipes/extract', {
        params: { url, language },
      })
      return res.data
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['recipes', 'me'] })
      queryClient.setQueryData(['recipes', 'me', res.id], res)
      // to update credits
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export const useShareRecipeMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await api.post(`/recipes/${id}/share`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes', 'me'] })
    },
  })
}

export const useUnshareRecipeMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await api.delete(`/recipes/${id}/share`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes', 'me'] })
    },
  })
}
